import { useEffect, useState, useRef } from 'react';

import { useAccountCompanyId } from 'application/hooks';

import {
  CompanyPlaybackReportSettingTypeFragment,
  CompanyReportTypeFragment,
  PlaybackReportType,
  useCompanyPlaybackReportSettingLazyQuery,
  useCompanyPlaybackReportsLazyQuery,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import { ReportTypes, TabsToReportTypeMap } from 'application/pages/ReportsPage/renderer/consts';

const useCompanyPlaybackReports = (reportType: PlaybackReportType | null, tab: ReportTypes | PlaybackReportType) => {
  const [currentRightholderSettings, setCurrentRightholderSettings] = useState<
    CompanyPlaybackReportSettingTypeFragment | undefined
  >();

  const [reports, setReports] = useState<CompanyReportTypeFragment[]>([]);
  const [reportsPollInterval, setReportsPollInterval] = useState<number | undefined>(undefined);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const isInitialLoadCompleted = useRef(false);
  const pollingStarted = useRef(false);

  const [settingsRequest, result] = useCompanyPlaybackReportSettingLazyQuery({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });

  const companyId = useAccountCompanyId();

  const [reportsRequest, reportsResult] = useCompanyPlaybackReportsLazyQuery({
    fetchPolicy: FetchPolicyKeys.NO_CACHE,
    variables: {
      companyId,
      filters: {
        reportType,
        adcampaignReportTypes: TabsToReportTypeMap[tab],
      },
    },
    pollInterval: reportsPollInterval,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (reportsResult.called && reportsResult.data && !isInitialLoadCompleted.current) {
      setIsInitialLoading(false);
      isInitialLoadCompleted.current = true;
    }
  }, [reportsResult.called, reportsResult.data]);

  useEffect(() => {
    setIsInitialLoading(true);
    isInitialLoadCompleted.current = false;
    pollingStarted.current = false;
  }, [companyId, reportType, tab]);

  useEffect(() => {
    if (reportsPollInterval && !pollingStarted.current) {
      pollingStarted.current = true;
    }
  }, [reportsPollInterval]);

  const loadSettings = async () => {
    const settings = await settingsRequest({
      variables: {
        companyId,
      },
    });

    return settings.data?.result;
  };

  useEffect(() => {
    setReports(reportsResult.data?.result || []);
  }, [reportsResult.data?.result]);

  const loadCompanyPlaybackReports = async (rightholderId: string) => {
    const reports = await reportsRequest({
      variables: {
        companyId,
        filters: {
          rightholderId,
        },
      },
    });

    return reports?.data?.result;
  };

  const loadCompanyFirstRightholderReport = async () => {
    const settings = await loadSettings();

    const firstRightholder = settings?.find((current) => current.isGenerateReport);
    setCurrentRightholderSettings(firstRightholder);

    setIsInitialLoading(true);
    isInitialLoadCompleted.current = false;

    await reportsResult.refetch();
  };

  const stopReportsPooling = () => {
    reportsResult.stopPolling();
    setReportsPollInterval(undefined);
    pollingStarted.current = false;
  };

  const startReportsPooling = (pollInterval: number) => {
    setReportsPollInterval(pollInterval);
  };

  return {
    loadSettings,
    error: result.error,
    loading: isInitialLoading || (result.loading && !reportsResult.data?.result),
    settings: result.data?.result || [],
    loadCompanyPlaybackReports,
    loadCompanyFirstRightholderReport,
    currentRightholderSettings,
    reports,
    companyId,
    refetchReports: reportsResult.refetch,
    stopReportsPooling,
    startReportsPooling,
  };
};

export default useCompanyPlaybackReports;
